import React, {type ReactNode} from 'react'
import {FormattedDate} from 'react-intl'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import minus from '@vio/atlas/utils/minus'

import {TimelineConnector} from './TimelineConnector'
import {TimelineDot} from './TimelineDot'

interface Props {
  topComponent: ReactNode
  bottomComponent: ReactNode
  middleComponent: ReactNode
  customGap?: boolean
  dates?: [string, string, string]
}

const TransitionIcon = styled(Icon)`
  transform: rotate(90deg);
`
const DateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${minus(cssTheme.layout.spacing.s200)};
  margin-bottom: ${minus(cssTheme.layout.spacing.s200)};
  flex-shrink: 0;
`

const TimelineRow = ({
  component,
  placement
}: {
  component: ReactNode
  placement: 'top' | 'center' | 'bottom'
}) => (
  <Grid container spacing="s000" wrap="nowrap" gap="s400">
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="s200"
      marginLeft={placement === 'center' ? '-3px' : 's000'}
    >
      {placement === 'top' && (
        <>
          <TimelineDot />
          <TimelineConnector />
        </>
      )}
      {placement === 'center' && (
        <>
          <TimelineConnector type="dashed" />
          <TransitionIcon
            name="Refresh3"
            size="sm"
            colorPath="content.warning.secondary"
          />
          <TimelineConnector type="dashed" />
        </>
      )}
      {placement === 'bottom' && (
        <>
          <TimelineConnector />
          <TimelineDot />
        </>
      )}
    </Box>
    {component}
  </Grid>
)
export const SplitBookingTimeline = ({
  topComponent,
  middleComponent,
  bottomComponent,
  customGap = false,
  dates
}: Props) => (
  <Box paddingTop={customGap ? 's000' : 's250'}>
    <Box display="flex" gap="s300">
      {dates && (
        <DateWrapper>
          {dates.map(date => (
            <Text variant="labelS" key={date}>
              <FormattedDate
                value={date}
                day="numeric"
                month="short"
                timeZone="UTC"
              />
            </Text>
          ))}
        </DateWrapper>
      )}
      <Grid container spacing="s000" gap={customGap ? 's000' : 's400'}>
        <TimelineRow placement="top" component={topComponent} />
        <TimelineRow placement="center" component={middleComponent} />
        <TimelineRow placement="bottom" component={bottomComponent} />
      </Grid>
    </Box>
  </Box>
)
